// BILL-20a — the frontend's view of one capability: is it released in this
// deployment, and does the server's best-of gate tier entitle the caller?
// A DISPLAY decision only: the server gate (FeatureGate, 241) is the boundary.
// Design: docs/plans/2026-10-05-bill20a-tier-gate-foundation-design.md §8.
import { useMemo } from 'react';
import { useFeature } from '@/lib/featureFlags';
import { type CapabilityView, useEntitlementStore } from './entitlementStore';

export type CapabilityEntitlement = 'unknown' | 'entitled' | 'locked';

export interface CapabilityState {
  /** The deployment ships it (catalog flag on, or an id with no flag). */
  released: boolean;
  /** `unknown` whenever the server sent no verdict for the id — never `entitled`. */
  entitlement: CapabilityEntitlement;
  /** Lowest tier that grants it, '' when unknown. */
  requiredTier: string;
}

/** Capability ids with no deployment flag of their own (design §8); every
 *  other id is released iff its catalog flag is on. */
const UNFLAGGED_CAPABILITIES: ReadonlySet<string> = new Set(['recursive_research', 'mcp_write']);

function entitlementOf(view: CapabilityView | undefined): CapabilityEntitlement {
  if (view === undefined) return 'unknown';
  return view.entitled ? 'entitled' : 'locked';
}

export function useCapability(id: string): CapabilityState {
  const flagOn = useFeature(id);
  const view = useEntitlementStore((s) => s.capabilities?.[id]);
  const released = UNFLAGGED_CAPABILITIES.has(id) || flagOn;
  const entitlement = entitlementOf(view);
  const requiredTier = view?.required_tier ?? '';
  // Memoized on primitives — a fresh object per render busts consumers' memos.
  return useMemo(
    () => ({ released, entitlement, requiredTier }),
    [released, entitlement, requiredTier],
  );
}

/** Non-reactive entitlement for code outside render (settings predicates). */
export function getCapabilityEntitlement(id: string): CapabilityEntitlement {
  return entitlementOf(useEntitlementStore.getState().capabilities?.[id]);
}

/** BILL-20a (D11): the best-of gate tier, '' when the server sent none. NOT the
 *  display tier (subscriptionStore.tierName). */
export function useGateTier(): string {
  return useEntitlementStore((s) => s.gateTier);
}
